import Component from "../../lib/component";
import {PaneSlideButton} from "./pane_slide_button";
import {PaneSlideFlipper} from "./pane_slide_flipper";
import {getThrottledFunction} from "../../helpers/throttle";
import {observeWhenLaidOut, entryHasArea} from "../../helpers/laid_out_intersection_observer";
import {PaneSlideFlip} from "./pane_slide_flip";

export const PaneSlideConfig = {
    componentTypeKey: 'PaneSlide',
    baseHtmlClass: 'pane_slides__slide',
    selector: '.pane_slides__slide',

    datasetConfigKey: 'pane_slide__config',
};

export class PaneSlide extends Component {

    static get config() {
        return PaneSlideConfig;
    }

    constructor(id_or_element, paneSlides) {
        super({
            element: id_or_element,
            config: PaneSlide.config
        });

        this.paneSlides = paneSlides;

        this.setDatasetConfig(PaneSlideConfig.datasetConfigKey);
        this.slideId = this.getSlideId();

        // Slide semantics for screen readers; a slide with no accessible name of its own gets a
        // positional one. Markup-provided attributes win.
        this.element.setAttribute('aria-roledescription', 'slide');
        if (!this.element.hasAttribute('aria-label')) {
            this.element.setAttribute('aria-label', `Slide ${this.getSlidePosition() + 1}`);
        }

        // Adopted for the same reason the carousel adopts its slides — see PaneSlides' constructor.
        this.flipSides = this.adoptChildComponents(getPaneSlideFlips(this));
        this.isFlipped = false;

        // These will be configured by the parent PaneSlides component.
        this.prevSlide = null;
        this.nextSlide = null;

        this.isFocused = null;

        this.paneSlideButtons = this.adoptChildComponents(getPaneSlideButtons(this));
        this.paneSlideFlippers = this.adoptChildComponents(getPaneSlideFlippers(this));

        // Watch for when the element is in the viewport and set it's 'focused' state accordingly.
        // The observe is deferred until the slide actually has area — nothing an
        // IntersectionObserver reports before then is worth believing (see the shared helper and
        // doc/README--zero_area_render_defects.md). Both the slide and its collapsing carousel
        // ancestor are watched, because a slide can be the last to resolve a height.
        const handler = getThrottledFunction(this.handleIntersection.bind(this), 100);
        this.observer = new IntersectionObserver(handler, {threshold: 0.75});
        this.onDestroy(() => this.observer.disconnect());
        const laidOutObserver = observeWhenLaidOut(this.observer, this.element, {
            alsoWatch: [this.paneSlides.element],
        });
        if (laidOutObserver) this.onDestroy(() => laidOutObserver.disconnect());

        // `slideTo`'s timer outlives the element, so it is registered per onDestroy's contract.
        this.slideToTimeoutId = null;
        this.onDestroy(() => window.clearTimeout(this.slideToTimeoutId));
    }

    handleIntersection(entries) {
        // The NEWEST queued observation — `entries[0]` is the oldest, so behind the 100ms throttle a
        // fast scroll could focus a slide which has already left. `entryHasArea` refuses a zero-area
        // delivery (the ratio-1 lie), covering a collapse AFTER the defer gate opened — the two are
        // not alternatives; see the shared helper.
        const entry = entries[entries.length - 1];
        if (entry.isIntersecting && entryHasArea(entry)) {
            this.paneSlides.setFocusedSlide(this);
            this.paneSlides.handleSlideFocused(this); // Analytics — only when the slide is genuinely on screen.
        }
        this.paneSlides.render();
    }

    // The slide's analytics identifier. Slides that don't declare one (the info panes' carousels)
    // fall back to their ordinal, which still tells us how far into a carousel people get.
    getSlideId() {
        const configuredSlideId = this.getDatasetConfigValue('slide_id');
        if (configuredSlideId) return configuredSlideId;
        return `SLIDE_${ this.getSlidePosition() + 1 }`;
    }

    // Read from the DOM rather than from the parent's `slides` — the parent is still building that
    // array when it constructs this slide.
    getSlidePosition() {
        const siblings = Array.from(this.paneSlides.element.querySelectorAll(PaneSlideConfig.selector));
        return siblings.indexOf(this.element);
    }

    render() {
        this.conditionallySetHtmlClassState(this.inited, 'inited');
        this.conditionallySetHtmlClassState(this.isFocused, 'focused');
        this.conditionallySetHtmlClassState(this.isFlipped, 'flipped');
        this.flipSides.forEach(flipSide => flipSide.render());
        this.paneSlideButtons.forEach(button => button.render());
        this.paneSlideFlippers.forEach(flipper => flipper.render());
    }

    setPrevSlide(paneSlide) {
        this.prevSlide = paneSlide;
    }

    setNextSlide(paneSlide) {
        this.nextSlide = paneSlide;
    }

    getPrevSlide() {
        return this.prevSlide;
    }

    getNextSlide() {
        return this.nextSlide;
    }

    slideTo(toPaneSlide) {
        this.paneSlides.isSliding = true;
        this.paneSlides.render();
        toPaneSlide.element.scrollIntoView({behavior: 'smooth'});
        // Clears THIS slide's own previous timer, so repeat taps on one slide don't stack.
        // Note it does not reach across slides: `isSliding` is carousel state but the timer is
        // per-slide, so a fast next-next sequence (a different slide each time) can still have an
        // earlier timer drop `isSliding` mid-move. Pre-existing and cosmetic — `isSliding` only
        // drives a CSS class — and fixing it properly means moving the timer onto `PaneSlides`.
        window.clearTimeout(this.slideToTimeoutId);
        this.slideToTimeoutId = window.setTimeout(() => {
            this.paneSlides.isSliding = false;
            this.paneSlides.render();
        }, 300);
    }

}

function getPaneSlideButtons(paneSlide) {
    const elements = Array.from(paneSlide.element.querySelectorAll(PaneSlideButton.config.selector));
    // If the existing HTML doesn't have a button, create one.
    if (elements.length === 0) {
        const prevButtonElement = PaneSlideButton.createElement(PaneSlideButton.config.prevKindCode);
        paneSlide.element.insertBefore(prevButtonElement, paneSlide.element.firstChild);
        elements.push(prevButtonElement);

        const nextButtonElement = PaneSlideButton.createElement(PaneSlideButton.config.nextKindCode);
        paneSlide.element.appendChild(nextButtonElement);
        elements.push(nextButtonElement);
    }
    return elements.map(element => new PaneSlideButton(element, paneSlide));
}

function getPaneSlideFlippers(paneSlide) {
    const elements = Array.from(paneSlide.element.querySelectorAll(PaneSlideFlipper.config.selector));
    return elements.map(element => new PaneSlideFlipper(element, paneSlide));
}

function getPaneSlideFlips(paneSlide) {
    const elements = Array.from(paneSlide.element.querySelectorAll(PaneSlideFlip.config.selector));
    return elements.map(element => new PaneSlideFlip(element, paneSlide));
}